// GA4 Part 1: resource facet search tracking via dataLayer.push
const SEARCH_SURFACE_SELECTOR = '[data-search-surface="resource-index"]'
const SEARCH_INPUT_SELECTOR = '[data-track-search-input="true"]'
const RESULT_LINK_SELECTOR = '[data-search-result-click="true"]'
const RESULT_ITEM_SELECTOR = '[data-search-result-item="true"]'
const FACET_CONTROL_SELECTOR = '[data-track-facet-control="true"]'
const DEBOUNCE_MS = 650

let searchQueryTimer = null
let searchQueryFallbackTimer = null
let pendingSearchQuery = null
let resultsViewTimer = null
let pendingFacetEvents = []
let facetFallbackTimer = null
let lastResultsViewSignature = null

function ensureDataLayer() {
  window.dataLayer = window.dataLayer || []
  return window.dataLayer
}

function pushDataLayerEvent(payload) {
  // Centralized GA4/GTM event push helper.
  ensureDataLayer().push(payload)
}

function parseCount(value) {
  const parsed = Number.parseInt(value ?? '', 10)
  return Number.isNaN(parsed) ? 0 : parsed
}

function toPath(urlValue) {
  if (!urlValue) {
    return ''
  }

  try {
    const parsed = new URL(urlValue, window.location.origin)
    return parsed.pathname
  } catch (_error) {
    return urlValue
  }
}

function getTotalResultsCount(surface) {
  if (!surface) {
    return 0
  }

  const resultsContainer = surface.querySelector('#results')
  const totalFromResults = parseCount(resultsContainer?.dataset?.totalResultsCount)
  if (totalFromResults > 0 || resultsContainer?.dataset?.totalResultsCount === '0') {
    return totalFromResults
  }

  return parseCount(surface.dataset.resultsCount)
}

function getSurfaceState(surface) {
  if (!surface) {
    return {
      searchTerm: '',
      activeFacets: '',
      resultsCount: 0,
      page: '1'
    }
  }

  return {
    searchTerm: surface.dataset.searchTerm ?? '',
    activeFacets: surface.dataset.activeFacets ?? '',
    resultsCount: getTotalResultsCount(surface),
    page: surface.dataset.resultsPage ?? '1'
  }
}

function getResultPosition(resultItem, surface) {
  const explicitPosition = parseCount(resultItem?.dataset?.resultPosition)
  if (explicitPosition > 0) {
    return explicitPosition
  }

  const items = Array.from(surface.querySelectorAll(RESULT_ITEM_SELECTOR))
  const index = items.indexOf(resultItem)
  return index >= 0 ? index + 1 : 0
}

function flushPendingSearchQuery() {
  if (!pendingSearchQuery) {
    return
  }

  const surface = document.querySelector(SEARCH_SURFACE_SELECTOR)
  if (!surface) {
    return
  }

  const state = getSurfaceState(surface)

  // Wait for Sprig state to catch up to the debounced input value.
  if (state.searchTerm !== pendingSearchQuery.term) {
    return
  }

  pushDataLayerEvent({
    event: 'search_query',
    search_term: state.searchTerm,
    results_count: state.resultsCount
  })

  pendingSearchQuery = null
  window.clearTimeout(searchQueryFallbackTimer)
}

function queueSearchQuery(term) {
  pendingSearchQuery = {
    term: term ?? ''
  }

  window.clearTimeout(searchQueryFallbackTimer)
  // Fallback so GA4 search_query still emits if Sprig render timing is delayed.
  searchQueryFallbackTimer = window.setTimeout(() => {
    const surface = document.querySelector(SEARCH_SURFACE_SELECTOR)
    if (!surface || !pendingSearchQuery) {
      return
    }

    const state = getSurfaceState(surface)
    pushDataLayerEvent({
      event: 'search_query',
      search_term: pendingSearchQuery.term,
      results_count: state.resultsCount
    })
    pendingSearchQuery = null
  }, 1500)
}

function queueFacetEvent(meta) {
  pendingFacetEvents.push(meta)

  window.clearTimeout(facetFallbackTimer)
  // Fallback ensures facet_apply is sent for the latest settled interaction.
  facetFallbackTimer = window.setTimeout(() => {
    flushFacetEvents()
  }, 1200)
}

function flushFacetEvents() {
  if (!pendingFacetEvents.length) {
    return
  }

  const surface = document.querySelector(SEARCH_SURFACE_SELECTOR)
  const state = getSurfaceState(surface)
  const eventsToFlush = pendingFacetEvents
  pendingFacetEvents = []

  eventsToFlush.forEach((meta) => {
    pushDataLayerEvent({
      event: 'facet_apply',
      facet_group: meta.facetGroup,
      facet_value: meta.facetValue,
      facet_action: meta.facetAction,
      search_term: state.searchTerm,
      active_facets: state.activeFacets,
      results_count: state.resultsCount
    })
  })
}

function queueResultsViewEvent() {
  window.clearTimeout(resultsViewTimer)
  resultsViewTimer = window.setTimeout(() => {
    const surface = document.querySelector(SEARCH_SURFACE_SELECTOR)
    if (!surface) {
      return
    }

    const state = getSurfaceState(surface)
    const signature = [
      state.searchTerm,
      state.activeFacets,
      state.resultsCount,
      state.page
    ].join('||')

    // Prevent duplicate GA4 search_results_view pushes for unchanged state.
    if (signature === lastResultsViewSignature) {
      return
    }

    lastResultsViewSignature = signature

    pushDataLayerEvent({
      event: 'search_results_view',
      search_term: state.searchTerm,
      active_facets: state.activeFacets,
      results_count: state.resultsCount
    })
  }, DEBOUNCE_MS)
}

function handleKeywordInput(event) {
  const input = event.target.closest(SEARCH_INPUT_SELECTOR)
  if (!input) {
    return
  }

  window.clearTimeout(searchQueryTimer)
  searchQueryTimer = window.setTimeout(() => {
    queueSearchQuery(input.value ?? '')
    flushPendingSearchQuery()
  }, DEBOUNCE_MS)
}

function handleResultClick(event) {
  const resultLink = event.target.closest(RESULT_LINK_SELECTOR)
  if (!resultLink) {
    return
  }

  // Only track clicks that occur inside the search surface container.
  const surface = resultLink.closest(SEARCH_SURFACE_SELECTOR)
  if (!surface) {
    return
  }

  const state = getSurfaceState(surface)
  const resultItem = resultLink.closest(RESULT_ITEM_SELECTOR)
  const resultUrl = toPath(resultItem?.dataset?.resultUrl || resultLink.getAttribute('href'))

  pushDataLayerEvent({
    event: 'search_result_click',
    search_term: state.searchTerm,
    active_facets: state.activeFacets,
    result_title: resultItem?.dataset?.resultTitle ?? resultLink.textContent?.trim() ?? '',
    result_position: getResultPosition(resultItem, surface),
    result_url: resultUrl
  })
}

function facetMetaFromElement(element) {
  const facetGroup = element.dataset.facetGroup ?? ''
  const facetValue = element.dataset.facetValue ?? element.value ?? ''
  const facetAction = element.dataset.facetAction ?? 'add'

  return {
    facetGroup,
    facetValue,
    facetAction
  }
}

function handleFacetInputChange(event) {
  const control = event.target.closest(`input${FACET_CONTROL_SELECTOR}`)
  if (!control) {
    return
  }

  queueFacetEvent(facetMetaFromElement(control))
}

function handleFacetRemoveClick(event) {
  const control = event.target.closest(`button${FACET_CONTROL_SELECTOR}`)
  if (!control) {
    return
  }

  queueFacetEvent(facetMetaFromElement(control))
}

function handleSprigRender() {
  flushPendingSearchQuery()
  flushFacetEvents()
  queueResultsViewEvent()
}

function initSearchTracking() {
  if (!document.querySelector(SEARCH_SURFACE_SELECTOR)) {
    return
  }

  ensureDataLayer()

  document.addEventListener('input', handleKeywordInput)
  document.addEventListener('change', handleFacetInputChange, true)
  document.addEventListener('click', handleFacetRemoveClick, true)
  document.addEventListener('click', handleResultClick, true)
  document.addEventListener('sprig:render', handleSprigRender)

  queueResultsViewEvent()
}

initSearchTracking()
